import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import axiosConn from '../../axiosConn';
import './localBusinessSpotlight.css';

const INITIAL_FORM = {
  name: '',
  phone: '',
  email: '',
  websiteConfirmation: '',
};

const OFFER_ENDPOINT =
  'local-business-spotlight/offers/busby-tire-oil-change/leads';
const BUSINESS_PHONE_DISPLAY = '731-584-6973';
const BUSINESS_PHONE_TEL = '17315846973';
const BUSINESS_ADDRESS = '278 Post Oak Ave, Camden, TN 38320';

const OFFER_STEPS = [
  'Enter your details',
  'Reveal your code',
  'Show it at Busby',
];

const SERVICES = [
  'Oil Changes',
  'Tires',
  'Alignments',
  'Brakes',
  'Batteries',
  'Mufflers & Exhaust',
  'Preventative Maintenance',
];

const TRUST_CARDS = [
  ['Expert Service', 'Top-quality care for your vehicle.'],
  ['Full-Service Shop', 'Tires, alignments, brakes & more.'],
  ['Local & Reliable', 'Proudly serving Camden since 1979.'],
];

const getApiMessage = (error) =>
  error?.response?.data?.message ||
  'We could not unlock the offer right now. Please try again.';

const validateForm = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Enter your name.';
  if (!form.phone.trim()) errors.phone = 'Enter your phone number.';
  if (!form.email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  return errors;
};

export default function BusbyTireOffer() {
  const source = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('src') || params.get('source') || '';
  }, []);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [claimReceived, setClaimReceived] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  useEffect(() => {
    document.documentElement.classList.add('spotlight-page-active');
    document.body.classList.add('spotlight-page-active');
    return () => {
      document.documentElement.classList.remove('spotlight-page-active');
      document.body.classList.remove('spotlight-page-active');
    };
  }, []);

  const resetOffer = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setSubmitError('');
    setClaimReceived(false);
    setCouponCode('');
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    const nextForm = { ...form, [name]: value };
    setForm(nextForm);
    setErrors(validateForm(nextForm));
  };

  const markTouched = (event) => {
    const { name } = event.target;
    setTouched((current) => ({ ...current, [name]: true }));
    setErrors(validateForm(form));
  };

  const getFieldError = (field) => {
    if (!submitAttempted && !touched[field]) return '';
    return errors[field] || '';
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors = validateForm(form);
    setSubmitAttempted(true);
    setErrors(nextErrors);
    setSubmitError('');
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    try {
      const response = await axiosConn.post(OFFER_ENDPOINT, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        websiteConfirmation: form.websiteConfirmation,
        source,
      });
      setCouponCode(String(response?.data?.data?.couponCode || '').trim());
      setClaimReceived(true);
    } catch (error) {
      setSubmitError(getApiMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (claimReceived) {
    if (!couponCode) {
      return (
        <main className="spotlight-page spotlight-offer-page">
          <Helmet>
            <title>Busby Tire request received | JG Local Business Spotlight</title>
            <meta
              name="description"
              content="Your Busby Tire & Alignment offer request was received."
            />
          </Helmet>
          <section className="spotlight-offer-hero spotlight-busby-hero">
            <div className="spotlight-shell spotlight-offer-success spotlight-busby-success">
              <p className="spotlight-eyebrow spotlight-busby-eyebrow">
                JG LOCAL BUSINESS SPOTLIGHT
              </p>
              <h1>Thanks, your request was received.</h1>
              <p>Busby Tire & Alignment has your submitted offer request details.</p>
              <div className="spotlight-offer-business spotlight-busby-business-card">
                <strong>Busby Tire & Alignment</strong>
                <span>{BUSINESS_ADDRESS}</span>
                <a href={`tel:${BUSINESS_PHONE_TEL}`}>{BUSINESS_PHONE_DISPLAY}</a>
              </div>
            </div>
          </section>
        </main>
      );
    }

    return (
      <main className="spotlight-page spotlight-offer-page">
        <Helmet>
          <title>Busby Tire offer ready | JG Local Business Spotlight</title>
          <meta
            name="description"
            content="Your Busby Tire & Alignment oil change offer is ready."
          />
        </Helmet>
        <section className="spotlight-offer-hero spotlight-busby-hero">
          <div className="spotlight-shell spotlight-offer-success spotlight-busby-success">
            <p className="spotlight-eyebrow spotlight-busby-eyebrow">
              JG LOCAL BUSINESS SPOTLIGHT
            </p>
            <p className="spotlight-offer-kicker spotlight-busby-pill">
              Coupon unlocked
            </p>
            <h1>Your $5 off oil change offer is ready.</h1>
            <div
              className="spotlight-coupon spotlight-busby-coupon"
              aria-label={`Coupon code ${couponCode}`}
            >
              <span>Coupon code</span>
              <strong>{couponCode}</strong>
            </div>
            <p>
              Show this screen or mention {couponCode} at Busby Tire &
              Alignment.
            </p>
            <div className="spotlight-offer-business spotlight-busby-business-card">
              <strong>Busby Tire & Alignment</strong>
              <span>{BUSINESS_ADDRESS}</span>
              <a href={`tel:${BUSINESS_PHONE_TEL}`}>{BUSINESS_PHONE_DISPLAY}</a>
            </div>
            <p className="spotlight-offer-disclaimer">
              One offer per customer. Valid for oil change service at Busby Tire
              & Alignment.
            </p>
            <div className="spotlight-busby-success-actions">
              <a
                className="spotlight-submit spotlight-busby-call"
                href={`tel:${BUSINESS_PHONE_TEL}`}
              >
                Call Busby
              </a>
              <button
                type="button"
                className="spotlight-busby-secondary"
                onClick={resetOffer}
              >
                Claim another offer
              </button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="spotlight-page spotlight-offer-page">
      <Helmet>
        <title>Busby Tire $5 oil change offer | JG Local Business Spotlight</title>
        <meta
          name="description"
          content="Claim a $5 off oil change offer from Busby Tire & Alignment in Camden, Tennessee."
        />
      </Helmet>
      <section className="spotlight-offer-hero spotlight-busby-hero">
        <div className="spotlight-shell spotlight-busby-topbar">
          <p className="spotlight-eyebrow spotlight-busby-eyebrow">
            <span>JG</span> LOCAL BUSINESS SPOTLIGHT
          </p>
          <div className="spotlight-busby-trust-row" aria-label="Busby trust signals">
            <span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 3 5 6v5c0 4.4 2.7 7.9 7 10 4.3-2.1 7-5.6 7-10V6l-7-3Z" />
                <path d="m9 12 2 2 4-5" />
              </svg>
              Experienced
            </span>
            <span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M8.5 12.4 4.9 8.8a3.1 3.1 0 0 1 0-4.4 3.1 3.1 0 0 1 4.4 0l1.1 1.1" />
                <path d="m15.5 11.6 3.6 3.6a3.1 3.1 0 0 1 0 4.4 3.1 3.1 0 0 1-4.4 0l-1.1-1.1" />
                <path d="m8 16 8-8" />
              </svg>
              Trusted
            </span>
            <span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 21s7-5.1 7-11a7 7 0 0 0-14 0c0 5.9 7 11 7 11Z" />
                <path d="M12 10.5h.01" />
              </svg>
              Local
            </span>
          </div>
        </div>

        <div className="spotlight-shell spotlight-offer-grid spotlight-busby-grid">
          <div className="spotlight-offer-copy spotlight-busby-copy">
            <div className="spotlight-busby-brand-row">
              <div className="spotlight-busby-logo" aria-label="Busby Tire & Alignment badge">
                <div className="spotlight-busby-logo-stars" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <span className="spotlight-busby-logo-name">BUSBY</span>
                <strong>Tire & Alignment</strong>
                <svg
                  className="spotlight-busby-tools"
                  viewBox="0 0 120 74"
                  aria-hidden="true"
                  focusable="false"
                >
                  <g>
                    <path d="M18 58 82 13" />
                    <path d="M82 13c7-5 15-4 20 1l-10 7 7 9 11-6c1 8-2 15-9 19-6 4-13 4-19 1L33 68c-5 3-11 2-14-3-2-3-2-5-1-7Z" />
                  </g>
                  <g>
                    <path d="M102 58 38 13" />
                    <path d="M38 13c-7-5-15-4-20 1l10 7-7 9-11-6c-1 8 2 15 9 19 6 4 13 4 19 1l49 24c5 3 11 2 14-3 2-3 2-5 1-7Z" />
                  </g>
                </svg>
                <small>Est. 1979</small>
              </div>
              <p className="spotlight-offer-kicker spotlight-busby-pill">
                Busby Tire & Alignment
              </p>
            </div>
            <h1 aria-label="Get $5 off your next oil change at Busby.">
              Get $5 off your next oil change at Busby<span className="spotlight-busby-period">.</span>
            </h1>
            <p className="spotlight-lede">
              Claim your coupon and show it when you visit. Busby Tire &
              Alignment has been trusted in Camden since 1979.
            </p>
            <div
              className="spotlight-offer-steps spotlight-busby-steps"
              aria-label="How the offer works"
            >
              {OFFER_STEPS.map((step, index) => (
                <span key={step}>
                  <strong>{index + 1}</strong>
                  {step}
                </span>
              ))}
            </div>
            <div className="spotlight-offer-details spotlight-busby-business-card">
              <strong>Busby Tire & Alignment</strong>
              <span>{BUSINESS_ADDRESS}</span>
              <a href={`tel:${BUSINESS_PHONE_TEL}`}>{BUSINESS_PHONE_DISPLAY}</a>
            </div>
            <p className="spotlight-offer-disclaimer">
              One offer per customer. Valid for oil change service at Busby Tire
              & Alignment. Show your coupon screen or mention your coupon code
              when you visit.
            </p>
            <div className="spotlight-busby-services" aria-label="Busby services">
              {SERVICES.map((service) => (
                <span key={service}>{service}</span>
              ))}
            </div>
          </div>

          <form
            className="spotlight-offer-form spotlight-busby-form"
            onSubmit={submit}
            noValidate
          >
            <header>
              <span>Claim offer</span>
              <h2>Claim Your $5 Off Oil Change</h2>
              <p>
                No account needed. Takes less than 10 seconds. We'll reveal
                your coupon code instantly after a successful submit.
              </p>
            </header>

            <label>
              Name
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={updateField}
                onBlur={markTouched}
                autoComplete="name"
                aria-invalid={Boolean(getFieldError('name'))}
              />
              {getFieldError('name') ? (
                <small>{getFieldError('name')}</small>
              ) : null}
            </label>

            <label>
              Phone
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={updateField}
                onBlur={markTouched}
                autoComplete="tel"
                inputMode="tel"
                aria-invalid={Boolean(getFieldError('phone'))}
              />
              {getFieldError('phone') ? (
                <small>{getFieldError('phone')}</small>
              ) : null}
            </label>

            <label>
              Email
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={updateField}
                onBlur={markTouched}
                autoComplete="email"
                aria-invalid={Boolean(getFieldError('email'))}
              />
              {getFieldError('email') ? (
                <small>{getFieldError('email')}</small>
              ) : null}
            </label>

            <input
              type="text"
              name="websiteConfirmation"
              value={form.websiteConfirmation}
              onChange={updateField}
              className="spotlight-honeypot"
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />

            {submitError ? (
              <div className="spotlight-notice spotlight-notice--error" role="alert">
                {submitError}
              </div>
            ) : null}

            <p className="spotlight-offer-consent">
              By submitting, you agree that Busby Tire & Alignment and JG Local
              Business Spotlight may contact you about this offer.
            </p>
            <p className="spotlight-offer-trust">
              Trusted in Camden since 1979.
            </p>

            <button className="spotlight-submit" type="submit" disabled={submitting}>
              {submitting ? 'Claiming...' : 'Claim My $5 Off Oil Change'}
            </button>
          </form>
        </div>

        <div className="spotlight-shell spotlight-busby-value-row">
          {TRUST_CARDS.map(([title, copy]) => (
            <article key={title}>
              <strong>{title}</strong>
              <span>{copy}</span>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
